import { Injectable } from '@angular/core';

/**
 * Colour theme for the WMS sidebar + dashboard (client 8 only).
 * A theme is turned into CSS variables on <html>, and the class
 * `wms-theme-on` on <body> switches the themed styles on.
 * `wms-3d` on <body> adds the 3D depth effect (glossy, raised surfaces).
 */
export interface WmsTheme {
    id: string;
    name: string;
    family: 'light' | 'classic' | 'custom';
    depth: boolean;    // 3D effect on by default for this theme
    sidebar: string;   // sidebar base colour (gradient is derived from it)
    primary: string;   // active menu item, buttons, tabs, table headers
    accent: string;    // sidebar section titles + skyline
    sbActive?: string; // sidebar active menu item (defaults to primary)
    pageBg: string;    // dashboard page background
    total: string;     // "Total" department card
    cards: string[];   // 5 colours used for department cards and stat cards
}

export interface WmsCustomColours {
    sidebar: string;
    primary: string;
    accent: string;
    pageBg: string;
}

const STORAGE_KEY = 'wms_theme_v1';
const BODY_CLASS = 'wms-theme-on';
const DEPTH_CLASS = 'wms-3d';
const STYLE_ID = 'wms-theme-runtime';

export const WMS_THEME_PRESETS: WmsTheme[] = [
    // ---------------- Classic: deep sidebars ----------------
    {
        id: 'royal-navy', name: 'Royal Navy', family: 'classic', depth: false,
        // deep navy sidebar with a sky-blue active item
        sidebar: '#163578', primary: '#1565ff', accent: '#4fc3f7', sbActive: '#1aa7ec', pageBg: '#f5f7fa', total: '#dc2626',
        // card order matches the department cards: Civil (purple), Electrical (pink-red),
        // Street Lights (blue), Water (orange), 5th (green)
        cards: ['#6a4cf5', '#e8335a', '#1e88e5', '#f57c00', '#059669']
    },
    {
        id: 'emerald', name: 'Emerald Green', family: 'classic', depth: false,
        sidebar: '#0b5a48', primary: '#059669', accent: '#6ee7b7', pageBg: '#f3f8f6', total: '#b45309',
        cards: ['#047857', '#0d9488', '#0891b2', '#65a30d', '#ca8a04']
    },
    {
        id: 'royal-purple', name: 'Royal Purple', family: 'classic', depth: false,
        sidebar: '#3b1a78', primary: '#7c3aed', accent: '#c4b5fd', pageBg: '#f6f5fb', total: '#be185d',
        cards: ['#6d28d9', '#c026d3', '#4f46e5', '#db2777', '#0891b2']
    },
    {
        id: 'saffron', name: 'Saffron Heritage', family: 'classic', depth: false,
        sidebar: '#7a3410', primary: '#ea580c', accent: '#fcd34d', pageBg: '#fbf7f2', total: '#b91c1c',
        cards: ['#c2410c', '#b45309', '#a16207', '#9f1239', '#0f766e']
    },
    {
        id: 'ocean-teal', name: 'Ocean Teal', family: 'classic', depth: false,
        sidebar: '#0b5468', primary: '#0891b2', accent: '#67e8f9', pageBg: '#f2f8fa', total: '#0f766e',
        cards: ['#0e7490', '#0d9488', '#2563eb', '#0369a1', '#4f46e5']
    },
    {
        id: 'crimson', name: 'Crimson Executive', family: 'classic', depth: false,
        sidebar: '#5c1427', primary: '#be123c', accent: '#fda4af', pageBg: '#faf5f6', total: '#b91c1c',
        cards: ['#9f1239', '#c2410c', '#7e22ce', '#1d4ed8', '#0f766e']
    },
    {
        id: 'graphite', name: 'Graphite Gold', family: 'classic', depth: false,
        sidebar: '#334155', primary: '#d97706', accent: '#fbbf24', pageBg: '#f4f5f7', total: '#d97706',
        cards: ['#334155', '#475569', '#0f766e', '#b45309', '#1e40af']
    }
];

const DEFAULT_ID = 'royal-navy';

@Injectable({ providedIn: 'root' })
export class WmsThemeService {
    readonly presets = WMS_THEME_PRESETS;
    readonly classicPresets = WMS_THEME_PRESETS.filter((t) => t.family === 'classic');

    activeId: string = DEFAULT_ID;
    custom: WmsCustomColours = this._customFrom(this._preset(DEFAULT_ID));
    depth3d = false;

    /** Load the saved choice and apply it. */
    enable(): void {
        this._ensureStyles();
        this._load();
        this._apply(this.current());
    }

    /** Remove all theme variables, classes and runtime styles (used when the user is not client 8). */
    disable(): void {
        const root = document.documentElement;
        Array.from(root.style)
            .filter((p) => p.startsWith('--wms-'))
            .forEach((p) => root.style.removeProperty(p));
        document.body.classList.remove(BODY_CLASS, DEPTH_CLASS);
        const style = document.getElementById(STYLE_ID);
        if (style) {
            style.remove();
        }
    }

    current(): WmsTheme {
        if (this.activeId === 'custom') {
            return this._buildCustom(this.custom);
        }
        return this._preset(this.activeId);
    }

    selectPreset(id: string): void {
        const theme = this.presets.find((t) => t.id === id);
        if (!theme) {
            return;
        }
        this.activeId = id;
        this.depth3d = theme.depth;             // each theme brings its own default depth
        this.custom = this._customFrom(theme);  // custom fields start from the chosen preset
        this._save();
        this._apply(theme);
    }

    setCustom(field: keyof WmsCustomColours, value: string): void {
        if (!/^#[0-9a-fA-F]{6}$/.test(value)) {
            return;
        }
        this.custom = { ...this.custom, [field]: value.toLowerCase() };
        this.activeId = 'custom';
        this._save();
        this._apply(this._buildCustom(this.custom));
    }

    setDepth(on: boolean): void {
        this.depth3d = !!on;
        this._save();
        this._apply(this.current());
    }

    reset(): void {
        this.selectPreset(DEFAULT_ID);
    }

    // ---------------------------------------------------------------------------------------------
    // Private
    // ---------------------------------------------------------------------------------------------

    private _preset(id: string): WmsTheme {
        return this.presets.find((t) => t.id === id) || this.presets.find((t) => t.id === DEFAULT_ID);
    }

    private _ensureStyles(): void {
        if (document.getElementById(STYLE_ID)) {
            return;
        }
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent = WMS_THEME_RUNTIME_CSS;
        document.head.appendChild(style);
    }

    private _apply(t: WmsTheme): void {
        const light = luminance(t.sidebar) > 0.5;

        const vars: Record<string, string> = {
            // sidebar: light sidebars fade up to near-white, dark ones fade down to deep tones
            '--wms-sb-from': light ? shade(t.sidebar, 0.7) : shade(t.sidebar, -0.45),
            '--wms-sb-mid': light ? shade(t.sidebar, 0.35) : shade(t.sidebar, -0.22),
            '--wms-sb-to': t.sidebar,
            '--wms-sb-title': t.accent,
            '--wms-sb-text': light ? '#1e293b' : '#ffffff',
            '--wms-sb-icon': light ? shade(t.accent, 0.1) : '#ffffff',
            '--wms-sb-hover': light ? 'rgba(255, 255, 255, 0.65)' : 'rgba(255, 255, 255, 0.08)',
            '--wms-sb-hover-soft': light ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255, 255, 255, 0.04)',
            '--wms-sb-guide': light ? 'rgba(15, 23, 42, 0.12)' : 'rgba(255, 255, 255, 0.14)',
            '--wms-sb-brand-bg': light ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.06)',
            '--wms-sb-skyline-op': light ? '0.3' : '0.6',
            '--wms-sb-thumb': light ? 'rgba(15, 23, 42, 0.2)' : 'rgba(255, 255, 255, 0.22)',
            '--wms-sb-edge': light ? 'rgba(255, 255, 255, 0.9)' : 'rgba(255, 255, 255, 0.06)',
            '--wms-sb-sheen': light ? '0.55' : '0.07',
            // soft glow at the top / bottom of the sidebar (accent tint on dark sidebars)
            '--wms-sb-glow-rgb': light ? '255, 255, 255' : rgbList(t.accent),
            '--wms-sb-active': t.sbActive || t.primary,
            '--wms-sb-active-2': shade(t.sbActive || t.primary, 0.18),
            '--wms-sb-active-rgb': rgbList(t.sbActive || t.primary),

            '--wms-primary': t.primary,
            '--wms-primary-2': shade(t.primary, 0.18),
            '--wms-primary-dark': shade(t.primary, -0.4),
            '--wms-primary-soft': shade(t.primary, 0.9),
            '--wms-primary-rgb': rgbList(t.primary),
            '--wms-card-tint': shade(t.primary, 0.965),
            '--wms-page-bg': t.pageBg,
            '--wms-total': t.total,
            '--wms-total-2': shade(t.total, 0.18),
            '--wms-total-active': shade(t.total, -0.22),
            '--wms-total-rgb': rgbList(t.total)
        };
        t.cards.forEach((c, i) => {
            vars[`--wms-c${i}`] = c;
            vars[`--wms-c${i}-2`] = shade(c, 0.2);
            vars[`--wms-c${i}-rgb`] = rgbList(c);
        });

        const root = document.documentElement;
        Object.keys(vars).forEach((k) => root.style.setProperty(k, vars[k]));
        document.body.classList.add(BODY_CLASS);
        document.body.classList.toggle(DEPTH_CLASS, this.depth3d);
    }

    private _buildCustom(c: WmsCustomColours): WmsTheme {
        // Department/stat card colours are spread around the colour wheel from the primary colour
        const cards = [0, 45, 160, 205, 300].map((deg) => rotateHue(c.primary, deg));
        return {
            id: 'custom', name: 'Custom', family: 'custom', depth: this.depth3d,
            sidebar: c.sidebar, primary: c.primary, accent: c.accent, pageBg: c.pageBg,
            total: rotateHue(c.primary, 180), cards
        };
    }

    private _customFrom(t: WmsTheme): WmsCustomColours {
        return { sidebar: t.sidebar, primary: t.primary, accent: t.accent, pageBg: t.pageBg };
    }

    private _load(): void {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (!raw) {
                this.depth3d = this._preset(this.activeId).depth;
                return;
            }
            const saved = JSON.parse(raw);
            if (saved && saved.custom) {
                const ok = ['sidebar', 'primary', 'accent', 'pageBg']
                    .every((k) => /^#[0-9a-fA-F]{6}$/.test(saved.custom[k]));
                if (ok) {
                    this.custom = saved.custom;
                }
            }
            if (saved && (saved.id === 'custom' || this.presets.some((t) => t.id === saved.id))) {
                this.activeId = saved.id;
            }
            this.depth3d = typeof saved?.depth === 'boolean'
                ? saved.depth
                : (this.activeId === 'custom' ? false : this._preset(this.activeId).depth);
        } catch (e) {
            // storage blocked or corrupt - keep defaults
        }
    }

    private _save(): void {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: this.activeId, custom: this.custom, depth: this.depth3d }));
        } catch (e) {
            // storage blocked - theme still applies for this session
        }
    }
}

/** Relative luminance 0..1 (used to pick dark or light sidebar text). */
function luminance(hex: string): number {
    const [r, g, b] = toRgb(hex).map((v) => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

// -------------------------------------------------------------------------------------------------
// Colour helpers
// -------------------------------------------------------------------------------------------------

function toRgb(hex: string): [number, number, number] {
    const n = parseInt(hex.replace('#', ''), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function toHex(r: number, g: number, b: number): string {
    return '#' + [r, g, b]
        .map((v) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0'))
        .join('');
}

function rgbList(hex: string): string {
    return toRgb(hex).join(', ');
}

/** p > 0 mixes toward white, p < 0 mixes toward black (p in -1..1). */
function shade(hex: string, p: number): string {
    const [r, g, b] = toRgb(hex);
    const target = p < 0 ? 0 : 255;
    const a = Math.abs(p);
    return toHex(r + (target - r) * a, g + (target - g) * a, b + (target - b) * a);
}

function rotateHue(hex: string, deg: number): string {
    let [r, g, b] = toRgb(hex).map((v) => v / 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    let h = 0;
    let s = 0;
    if (max !== min) {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r) { h = (g - b) / d + (g < b ? 6 : 0); }
        else if (max === g) { h = (b - r) / d + 2; }
        else { h = (r - g) / d + 4; }
        h /= 6;
    }
    // keep custom card colours readable under white text
    s = Math.max(s, 0.55);
    const L = Math.min(Math.max(l, 0.32), 0.48);
    h = (h + deg / 360) % 1;

    const hue2rgb = (pp: number, q: number, t: number): number => {
        if (t < 0) { t += 1; }
        if (t > 1) { t -= 1; }
        if (t < 1 / 6) { return pp + (q - pp) * 6 * t; }
        if (t < 1 / 2) { return q; }
        if (t < 2 / 3) { return pp + (q - pp) * (2 / 3 - t) * 6; }
        return pp;
    };
    const q = L < 0.5 ? L * (1 + s) : L + s - L * s;
    const pp = 2 * L - q;
    return toHex(hue2rgb(pp, q, h + 1 / 3) * 255, hue2rgb(pp, q, h) * 255, hue2rgb(pp, q, h - 1 / 3) * 255);
}

// -------------------------------------------------------------------------------------------------
// Runtime styles (kept in this file so the theme has a single source file)
// -------------------------------------------------------------------------------------------------

/**
 * Runtime styles owned by the WMS theme (client 8 only).
 * Injected once by WmsThemeService as <style id="wms-theme-runtime">.
 * Every rule starts with body.wms-theme-on, so nothing applies unless the theme picker is active.
 *
 * Part 1: sidebar text/icon colours from variables (so light sidebars stay readable).
 * Part 2: optional 3D depth effect (body.wms-3d): glossy highlights, soft raised shadows.
 */

const SB = 'body.wms-theme-on fuse-vertical-navigation.wms-sidebar';
const ITEMS = ['basic', 'collapsable', 'aside']
    .map((t) => `${SB} fuse-vertical-navigation-${t}-item > .fuse-vertical-navigation-item-wrapper .fuse-vertical-navigation-item`);
const each = (suffix: string): string => ITEMS.map((s) => s + suffix).join(',\n');

const D3 = 'body.wms-theme-on.wms-3d';
const D3SB = `${D3} fuse-vertical-navigation.wms-sidebar`;
const D3ITEMS = ITEMS.map((s) => s.replace(SB, D3SB));
const each3d = (suffix: string): string => D3ITEMS.map((s) => s + suffix).join(',\n');
const DASH = `${D3} .wms-themable`;

const NOT_ACTIVE = ':not(.fuse-vertical-navigation-item-active):not(.fuse-vertical-navigation-item-active-forced)';

const WMS_THEME_RUNTIME_CSS = `
/* ===================== Part 1: sidebar colours ===================== */
${SB} { color: var(--wms-sb-text); }

${each(`${NOT_ACTIVE} .fuse-vertical-navigation-item-icon`)} {
    color: var(--wms-sb-icon) !important;
}
${each(`${NOT_ACTIVE} .fuse-vertical-navigation-item-title-wrapper .fuse-vertical-navigation-item-title`)},
${each(`${NOT_ACTIVE} .fuse-vertical-navigation-item-arrow`)} {
    color: var(--wms-sb-text) !important;
}
${each(`${NOT_ACTIVE}:not(.fuse-vertical-navigation-item-disabled):hover`)} {
    background: var(--wms-sb-hover) !important;
}
${SB} fuse-vertical-navigation-collapsable-item.fuse-vertical-navigation-item-expanded > .fuse-vertical-navigation-item-wrapper .fuse-vertical-navigation-item${NOT_ACTIVE} {
    background: var(--wms-sb-hover-soft) !important;
}
${SB} fuse-vertical-navigation-collapsable-item > .fuse-vertical-navigation-item-children,
${SB} fuse-vertical-navigation-collapsable-item > .fuse-vertical-navigation-item-children .fuse-vertical-navigation-item-children {
    border-left-color: var(--wms-sb-guide) !important;
}
${SB} .wms-sidebar-brand {
    background: var(--wms-sb-brand-bg) !important;
    border-color: var(--wms-sb-guide) !important;
}
${SB} .wms-sidebar-skyline { opacity: var(--wms-sb-skyline-op) !important; }
${SB} .fuse-vertical-navigation-wrapper .fuse-vertical-navigation-content {
    scrollbar-color: var(--wms-sb-thumb) transparent !important;
}
${SB} .fuse-vertical-navigation-wrapper .fuse-vertical-navigation-content::-webkit-scrollbar-thumb {
    background: var(--wms-sb-thumb) !important;
}

/* ===================== Part 2: 3D depth effect ===================== */

/* Sidebar: soft edge shadow + top sheen */
${D3SB} {
    box-shadow: 6px 0 24px rgba(15, 23, 42, 0.10), inset -1px 0 0 var(--wms-sb-edge);
}
${D3SB} .fuse-vertical-navigation-wrapper {
    background-image: linear-gradient(180deg, rgba(255, 255, 255, var(--wms-sb-sheen)) 0%, rgba(255, 255, 255, 0) 38%) !important;
}

/* Sidebar active item: glossy raised pill */
${each3d('.fuse-vertical-navigation-item-active')},
${each3d('.fuse-vertical-navigation-item-active-forced')} {
    background:
        linear-gradient(180deg, rgba(255, 255, 255, 0.30) 0%, rgba(255, 255, 255, 0.06) 50%, rgba(0, 0, 0, 0.06) 100%),
        linear-gradient(90deg, var(--wms-sb-active) 0%, var(--wms-sb-active-2) 100%) !important;
    box-shadow:
        0 8px 18px -6px rgba(var(--wms-sb-active-rgb), 0.65),
        0 2px 4px rgba(var(--wms-sb-active-rgb), 0.25),
        inset 0 1px 0 rgba(255, 255, 255, 0.55),
        inset 0 -2px 0 rgba(0, 0, 0, 0.12) !important;
    transform: translateY(-1px);
}

/* Sidebar hover: slight lift */
${each3d(`${NOT_ACTIVE}:hover`)} {
    box-shadow: 0 4px 10px -4px rgba(15, 23, 42, 0.18), inset 0 1px 0 rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
}

/* Dashboard header button + active tabs: glossy */
${DASH} .dashboard-header button.mat-primary:not([disabled]),
${DASH} .dash-main-tab-actv,
${DASH} .inbx-tab-actv {
    background-image:
        linear-gradient(180deg, rgba(255, 255, 255, 0.28) 0%, rgba(255, 255, 255, 0) 55%),
        linear-gradient(90deg, var(--wms-primary) 0%, var(--wms-primary-2) 100%) !important;
    box-shadow:
        0 6px 14px -4px rgba(var(--wms-primary-rgb), 0.55),
        inset 0 1px 0 rgba(255, 255, 255, 0.5),
        inset 0 -2px 0 rgba(0, 0, 0, 0.10) !important;
}

/* Department cards: only a faint top sheen. The card look itself (colours, border,
   shadow) belongs to the dashboard's own client-8 design, so it is not restyled here. */
${DASH} .department-card::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.14) 0%, rgba(255, 255, 255, 0) 45%);
}

/* Stat group cards: raised white panels with soft layered shadow */
${DASH} .sgc {
    background: linear-gradient(180deg, #ffffff 0%, var(--wms-card-tint) 100%) !important;
    border: 1px solid rgba(255, 255, 255, 0.9);
    box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.9) inset,
        0 2px 4px rgba(15, 23, 42, 0.04),
        0 12px 28px -12px rgba(var(--wms-primary-rgb), 0.28) !important;
}
${DASH} .sgc .sgc-icon-wrap {
    box-shadow:
        0 8px 16px -6px rgba(15, 23, 42, 0.35),
        inset 0 1px 0 rgba(255, 255, 255, 0.55),
        inset 0 -2px 0 rgba(0, 0, 0, 0.15) !important;
    position: relative;
    overflow: hidden;
}
${DASH} .sgc .sgc-icon-wrap::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.32) 0%, rgba(255, 255, 255, 0) 55%);
}
${DASH} .sgc .smc {
    background: linear-gradient(180deg, #ffffff 0%, var(--wms-card-tint) 100%) !important;
    box-shadow: 0 1px 0 #ffffff inset, 0 4px 10px -6px rgba(15, 23, 42, 0.18) !important;
}

/* Inbox panel: raised */
${DASH} .inbx-card {
    box-shadow:
        0 1px 0 rgba(255, 255, 255, 0.9) inset,
        0 12px 28px -14px rgba(var(--wms-primary-rgb), 0.30) !important;
}

/* Inactive pill tabs: subtle raised */
${DASH} .dash-main-tab:not(.dash-main-tab-actv),
${DASH} .inbx-tab:not(.inbx-tab-actv) {
    background: linear-gradient(180deg, #ffffff 0%, #f3f6fb 100%) !important;
    box-shadow: 0 2px 6px -2px rgba(15, 23, 42, 0.12), inset 0 1px 0 #ffffff;
}
`;
